import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { isAuthorizedAdminUser, signInAdmin, signOutAdmin } from './auth';

// Set once an admin session is established, cleared on explicit logout - lets
// onAuthStateChanged tell "genuinely never logged in" apart from "this browser
// should have a session, a null here is probably a transient re-hydration
// race" (e.g. after a full reload following back/forward navigation).
const HAD_ADMIN_SESSION_KEY = 'rivals-admin-had-session';
const LOGOUT_GRACE_MS = 700;

export default function useAdminSession() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(Boolean(auth));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    let logoutTimer = null;

    const commitLoggedOut = () => {
      if (!active) return;
      setUser(null);
      setIsAdmin(false);
      setLoading(false);
    };

    // Shared by the normal path and the grace-period recheck below, so that
    // whatever user Firebase settles on - the real admin restored, or some
    // other user entirely - always gets properly evaluated instead of being
    // silently ignored.
    const resolveUser = async (currentUser) => {
      setUser(currentUser);

      try {
        const authorized = await isAuthorizedAdminUser(currentUser);
        if (!active) return;
        setIsAdmin(authorized);
        if (authorized) {
          try {
            localStorage.setItem(HAD_ADMIN_SESSION_KEY, '1');
          } catch {
            // Best-effort marker only.
          }
        }
      } catch (sessionError) {
        if (!active) return;
        setError(sessionError.message || 'Unable to verify admin session.');
        setIsAdmin(false);
      } finally {
        if (active) setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!active) return;

      if (!currentUser) {
        let hadSession = false;
        try {
          hadSession = Boolean(localStorage.getItem(HAD_ADMIN_SESSION_KEY));
        } catch {
          // localStorage unavailable - fall back to committing immediately.
        }

        if (!hadSession) {
          commitLoggedOut();
          return;
        }

        // This browser should have a session - give Firebase a brief window
        // to correct a possibly-transient null before we act on it.
        if (logoutTimer) clearTimeout(logoutTimer);
        logoutTimer = setTimeout(() => {
          if (!active) return;
          if (auth.currentUser) {
            resolveUser(auth.currentUser);
          } else {
            commitLoggedOut();
          }
        }, LOGOUT_GRACE_MS);
        return;
      }

      if (logoutTimer) {
        clearTimeout(logoutTimer);
        logoutTimer = null;
      }

      await resolveUser(currentUser);
    });

    return () => {
      active = false;
      if (logoutTimer) clearTimeout(logoutTimer);
      unsubscribe();
    };
  }, []);

  const login = async (email, password, remember = true) => {
    setError('');
    setLoading(true);
    const currentUser = await signInAdmin(auth, email, password, remember);
    setUser(currentUser);
    setIsAdmin(true);
    try {
      localStorage.setItem(HAD_ADMIN_SESSION_KEY, '1');
    } catch {
      // Best-effort marker only.
    }
    return currentUser;
  };

  const logout = async () => {
    setError('');
    setLoading(true);
    await signOutAdmin(auth);
    setUser(null);
    setIsAdmin(false);
    setLoading(false);
    try {
      localStorage.removeItem(HAD_ADMIN_SESSION_KEY);
    } catch {
      // Best-effort marker only.
    }
  };

  return {
    user,
    isAdmin,
    loading,
    error,
    setError,
    login,
    logout,
  };
}